import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Permisos (de Auth0 RBAC) que dan acceso a la aplicación.
 * El usuario debe tener al menos uno en el claim `permissions` del access token.
 */
export const REQUIRED_PERMISSIONS = ["tech:edit"];

function decodeJwt(token: string): { permissions?: string[] } | null {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(
      atob(normalized)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Lee el claim `permissions` del access token de Auth0 y reporta si el usuario
 * actual puede acceder a la app.
 *
 * Requiere RBAC habilitado + "Add Permissions in the Access Token" en la API
 * del audience configurado en el Auth0Provider.
 */
export function usePermissions() {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      if (mounted) {
        setPermissions(null);
        setLoading(false);
      }
      return;
    }

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const claims = decodeJwt(token);
        if (mounted) {
          setPermissions(claims?.permissions ?? []);
        }
      } catch {
        if (mounted) {
          setPermissions([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  const hasAccess = !!permissions?.some((permission) => REQUIRED_PERMISSIONS.includes(permission));

  return { permissions, loading, hasAccess };
}
